/**
 * Marketing attribution capture (LH-114).
 *
 * Reads UTM params, `source`, and `ref` from the landing URL plus the
 * external referrer, then persists `{f: first_touch, l: last_touch}` in
 * localStorage. The payload is forwarded to `/auth/{provider}/start` as a
 * base64 `attr` query param when the visitor starts OAuth, and the API
 * bridges it through the provider round trip in a signed cookie before
 * persisting it onto the canonical users document.
 *
 * localStorage is used for persistence across sessions — this is
 * marketing metadata, not an auth token, so it does not violate the
 * no-tokens-in-browser-storage rule.
 */

const STORAGE_KEY = "lh_attribution";
const MAX_LEN = 200;
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = new RegExp("[\u0000-\u001f\u007f]", "g");

export interface AttributionTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref?: string;
  referrer?: string;
  landing_path?: string;
  captured_at?: string;
}

/** Compact stored shape: `f` = first touch (write-once), `l` = last touch. */
export interface StoredAttribution {
  f: AttributionTouch | null;
  l: AttributionTouch | null;
}

const TAG_KEYS: readonly (keyof AttributionTouch)[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "ref",
];

const TOUCH_KEYS: readonly (keyof AttributionTouch)[] = [
  ...TAG_KEYS,
  "referrer",
  "landing_path",
  "captured_at",
];

function clean(value: string | null | undefined): string | undefined {
  if (value == null) return undefined;
  const cleaned = String(value).replace(CONTROL_CHARS, "").trim();
  return cleaned ? cleaned.slice(0, MAX_LEN) : undefined;
}

/** Reduce a referrer URL to `scheme://host/path` — query/fragment may leak tokens. */
export function sanitizeReferrer(raw: string | null | undefined): string | undefined {
  const value = clean(raw);
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return undefined;
    return `${url.protocol}//${url.host}${url.pathname}`.slice(0, MAX_LEN);
  } catch {
    return undefined;
  }
}

function isIsoDate(value: string | undefined): boolean {
  if (!value) return false;
  return !Number.isNaN(Date.parse(value));
}

function sanitizeTouch(raw: unknown): AttributionTouch | null {
  if (!raw || typeof raw !== "object") return null;
  const input = raw as Record<string, unknown>;
  const touch: AttributionTouch = {};
  for (const key of TOUCH_KEYS) {
    const value = input[key === "utm_source" && !(key in input) ? "source" : key];
    let cleaned: string | undefined;
    if (key === "referrer") cleaned = sanitizeReferrer(value as string);
    else if (key === "captured_at") {
      cleaned = clean(value as string);
      if (cleaned && !isIsoDate(cleaned)) cleaned = undefined;
    } else cleaned = clean(value as string);
    if (cleaned) touch[key] = cleaned;
  }
  return Object.keys(touch).length ? touch : null;
}

/**
 * Parse a tagged touch from query params. Accepts the five `utm_*` keys,
 * `source` (alias for `utm_source`), and `ref` (creator/short-link code).
 * Returns null when no tag param is present.
 */
export function parseTouchFromParams(
  params: URLSearchParams,
  landingPath?: string,
  referrer?: string,
  now: Date = new Date(),
): AttributionTouch | null {
  const touch: AttributionTouch = {};
  for (const key of TAG_KEYS) {
    const value = clean(params.get(key));
    if (value) touch[key] = value;
  }
  const source = clean(params.get("source"));
  if (source && !touch.utm_source) touch.utm_source = source;

  if (!TAG_KEYS.some((k) => touch[k])) return null;

  const path = clean(landingPath);
  if (path) touch.landing_path = path;
  const ref = sanitizeReferrer(referrer);
  if (ref) touch.referrer = ref;
  touch.captured_at = now.toISOString();
  return touch;
}

/** Read and re-sanitize the stored attribution payload. */
export function readStoredAttribution(): StoredAttribution | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const f = sanitizeTouch((parsed as StoredAttribution).f);
    const l = sanitizeTouch((parsed as StoredAttribution).l);
    if (!f && !l) return null;
    return { f, l };
  } catch {
    return null;
  }
}

/**
 * Capture attribution for the current location.
 *
 * - Tagged visit (any utm_* / source / ref param): becomes `l`, and `f`
 *   if none exists yet.
 * - Organic first visit (external referrer, nothing stored): seeds both.
 * - Anything else: no-op; returns whatever is already stored.
 */
export function captureAttribution(
  search: string = window.location.search,
  pathname: string = window.location.pathname,
  referrer: string = document.referrer,
  currentHost: string = window.location.hostname,
): StoredAttribution | null {
  const stored = readStoredAttribution() ?? { f: null, l: null };
  const params = new URLSearchParams(search);

  let touch = parseTouchFromParams(params, pathname, referrer);
  if (!touch && !stored.f) {
    const ref = sanitizeReferrer(referrer);
    // Sanitized form is always scheme://host/path — the trailing slash makes
    // the host check exact (no `example.com.evil.com` false negatives).
    const selfRef = currentHost ? [`http://${currentHost}/`, `https://${currentHost}/`] : [];
    if (ref && !selfRef.some((prefix) => ref.startsWith(prefix))) {
      touch = {
        referrer: ref,
        landing_path: clean(pathname),
        captured_at: new Date().toISOString(),
      };
    }
  }

  if (!touch) return stored.f || stored.l ? stored : null;
  const next: StoredAttribution = { f: stored.f ?? touch, l: touch };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Private mode / storage disabled — attribution is best-effort.
  }
  return next;
}

/** Serialize stored attribution as a URL-safe base64 `attr` param value. */
export function encodeAttributionParam(): string | null {
  const stored = readStoredAttribution();
  if (!stored || (!stored.f && !stored.l)) return null;
  try {
    const bytes = new TextEncoder().encode(JSON.stringify(stored));
    let binary = "";
    bytes.forEach((b) => {
      binary += String.fromCharCode(b);
    });
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  } catch {
    return null;
  }
}

/** Exposed for tests. */
export const __testing = { STORAGE_KEY };
